import React, { useRef, useEffect, useState } from "react";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { connect } from "react-redux";
import {
  createSlotAndAppointment,
  createAppointmentWithBilling,
  deleteSlotAndAppointment,
  getPractitioners,
  getPractitioner,
  sendEmailAppointment,
  createNewPatientAndTask,
  getAvailableTimes,
  getAvailableMonthDates,
  getPractitionerSchedule,
  getLocation,
} from "../utils/asyncServerRequests";
import {
  getAvailabilityForPractitioner,
  sendEmailNotification,
  createApptMeta,
  getPaymentMethodConfig,
} from "../utils/asyncSettingsServerRequests";
import { withStyles, Hidden } from "@material-ui/core";
import {
  dropdown,
  date,
  renderCalendarPicker,
  text,
} from "./renderers/renderers";
import renderSubmitButton from "./renderers/SubmitFormButton";
import moment from "moment-timezone";
import { generateUuid } from "../utils/uuid";
import {
  createSlotResource,
  createAppointmentResource,
  createApptMetaResource,
  parseUserName,
  isObjEmpty,
} from "../utils/resources";
import { withSnackbar } from "notistack";
import EventSelectForm from "./subforms/EventSelectForm";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  REQUEST_AVAILABLE_SLOTS,
  FETCH_AVAILABLE_SLOTS_SUCCESS,
  FETCH_AVAILABLE_SLOTS_FAILED,
  SET_SCHEDULE_FETCHING,
  SET_APPOINTMENT_SUCCESS,
  FETCH_PRACTITIONERS_SUCCEEDED,
  FETCH_PRACTITIONERS_FAILED,
  FETCH_SCHEDULE_FAILED,
  FETCH_SCHEDULE_SUCCEEDED,
} from "../actions/actions";
import { errorClass } from "./consts";
import { STANDARD_DATE_FORMAT_NO_TIME } from "../consts";
import { useRainforestPay } from "../hooks/useRainforestPay";

const styles = theme => ({
  form: {
    display: "flex",
    flexDirection: "column",
    margin: "auto",
    minHeight: "50vh",
    maxHeight: "fit-content",
    minWidth: "fit-content",
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  buttonProgress: {
    position: "relative",
    left: "50%",
  },
  textField: {
    marginLeft: theme.spacing.unit,
    marginRight: theme.spacing.unit,
    width: "90%",
  },
  select: {
    minWidth: "fit-content",
  },
  dense: {
    marginTop: 19,
  },
  menu: {
    width: 200,
  },
  button: {
    margin: theme.spacing.unit,
  },
  input: {
    display: "none",
  },
  dialogPaper: {
    minHeight: "500",
  },
  paper: {
    minWidth: "fit-content",
    width: "80%",
  },
  error: {
    color: "red",
  },
});

// Server errors come back as either a string or an object like { error: "..." }.
// Passing an object to enqueueSnackbar throws "Objects are not valid as a React
// child", so always coerce to a string first.
const toErrorMessage = (value, fallback) => {
  if (typeof value === "string" && value) return value;
  if (value && typeof value === "object") {
    if (typeof value.error === "string" && value.error) return value.error;
    if (typeof value.message === "string" && value.message)
      return value.message;
  }
  return fallback;
};

// Rainforest reports the form invalid while it runs a BIN lookup on a completed
// card number, so wait briefly before treating "invalid" as incomplete details.
const BIN_LOOKUP_WAIT_MS = 1500;
// How long to give Rainforest to acknowledge a submit with `attempted`
const ATTEMPT_ACK_MS = 400;
// Backstop for a submit that was accepted but never produced a result
const PAYMENT_RESULT_TIMEOUT_MS = 30000;

let availability,
  scheduleId = null;
let timeSlotOptions = [];
let calendarDate = moment();

const AppointmentOptionsSchema = Yup.object().shape({
  event: Yup.string()
    .required("Required")
    .nullable(),
  date: Yup.date()
    .required("Required")
    .nullable(),
  timeSlot: Yup.string()
    .required("Required")
    .nullable(),
});

const AppointmentOptionsForm = props => {
  const {
    classes,
    enqueueSnackbar,
    events,
    fetching,
    fetched,
    scheduleFetched,
    error,
    getAvailableSlots,
    fetchAvailableSlotsSuccess,
    fetchAvailableSlotsFailed,
    setSettingsFetching,
    newPatient,
    fetchPractitionersFailed,
    fetchPractitionersSuccess,
  } = props;
  useRainforestPay();
  const [timeZone, setTimeZone] = useState(moment.tz.guess());
  const [practitionerUser, setPractitionerUser] = useState(null);
  const practitionerUserRef = useRef();
  const [practitioners, setPractitioners] = useState([]);
  const noEventsMessage = `Not able to book online. Please contact us directly at ${props.organization.phoneNumber}.`;
  const [eventOptions, setEventOptions] = useState([]);
  const [availableDates, setAvailableDates] = useState(null);
  const currentEventIdRef = useRef(null);
  const [fetchingDates, setFetchingDates] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'pending', 'approved', 'declined', 'error'
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [billingAcknowledged, setBillingAcknowledged] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const paymentComponentRef = useRef(null);
  const paymentResolveRef = useRef(null);
  const paymentRejectRef = useRef(null);
  const paymentTimeoutRef = useRef(null);
  // Rainforest's submit() silently does nothing when the form is invalid, so it
  // never emits approved/declined/error. Track validity to avoid that hang.
  const paymentValidRef = useRef(false);
  // Rainforest emits `attempted` the moment it accepts a submit, so its absence
  // tells us the submit was silently dropped
  const paymentAttemptedRef = useRef(false);

  const fetchMonthAvailability = async (practitionerId, eventId, date) => {
    const m = date || calendarDate;
    setFetchingDates(true);
    const response = await getAvailableMonthDates(
      practitionerId,
      eventId,
      m.year(),
      m.month() + 1,
      moment.tz.guess(),
      props.selectedPatient && props.selectedPatient.id
    );
    if (response.code < 400) {
      setAvailableDates(response.data);
    }
    setFetchingDates(false);
  };

  // Helper functions to get billing info from event
  const getBillingInfo = event => {
    if (!event || !event.event) return null;
    const billing = event.event.billing;
    if (!billing || !billing.enabled) return null;
    return billing;
  };

  const getBaseFeeInDollars = billing => {
    if (!billing || !billing.baseFee || !billing.baseFee.amountInCents)
      return 0;
    return billing.baseFee.amountInCents / 100;
  };

  const getNoShowFeeInDollars = billing => {
    if (!billing || !billing.noShowFee || !billing.noShowFee.amountInCents)
      return 0;
    return billing.noShowFee.amountInCents / 100;
  };

  const getLateCancelFeeInDollars = billing => {
    if (
      !billing ||
      !billing.lateCancelFee ||
      !billing.lateCancelFee.amountInCents
    )
      return 0;
    return billing.lateCancelFee.amountInCents / 100;
  };

  const getBillAt = billing => {
    if (!billing || !billing.baseFee) return null;
    return billing.baseFee.billAt;
  };

  const hasBookableSlotsInRange = event => {
    if (!availableDates || !event || !event.event) return false;
    const maxD = moment().add(event.event.dateRange, "days");
    return Object.entries(availableDates).some(([dateStr, slots]) => {
      if (slots.length === 0) return false;
      return moment(dateStr).isSameOrBefore(maxD, "day");
    });
  };

  const requiresPaymentCapture = billing => {
    if (!billing) return false;
    const hasBaseFee = billing.baseFee && billing.baseFee.amountInCents > 0;
    const hasNoShowFee =
      billing.noShowFee && billing.noShowFee.amountInCents > 0;
    const hasLateCancelFee =
      billing.lateCancelFee && billing.lateCancelFee.amountInCents > 0;
    return hasBaseFee || hasNoShowFee || hasLateCancelFee;
  };

  // Function to fetch payment method config for a given event
  // This creates a payment-method-config (not payin-config) so we just save the card
  // All charging happens server-side
  const fetchPaymentConfigForEvent = async event => {
    if (!event || !event.event) {
      setPaymentConfig(null);
      setPaymentStatus(null);
      setPaymentMethodId(null);
      setBillingAcknowledged(false);
      return;
    }

    const billing = getBillingInfo(event);

    // Check if we need to capture payment method (for any fee type)
    if (!requiresPaymentCapture(billing)) {
      setPaymentConfig(null);
      setPaymentStatus(null);
      setPaymentMethodId(null);
      setBillingAcknowledged(false);
      return;
    }

    const baseFeeInCents =
      billing && billing.baseFee ? billing.baseFee.amountInCents : 0;
    const noShowFeeInCents =
      billing && billing.noShowFee ? billing.noShowFee.amountInCents : 0;
    const lateCancelFeeInCents =
      billing && billing.lateCancelFee
        ? billing.lateCancelFee.amountInCents
        : 0;
    const billAt = getBillAt(billing);

    try {
      const response = await getPaymentMethodConfig(
        {
          eventId: event.event._id,
          billAt: billAt,
          baseFeeInCents: baseFeeInCents,
          noShowFeeInCents: noShowFeeInCents,
          lateCancelFeeInCents: lateCancelFeeInCents,
        },
        event.event._id,
        props.selectedPatient.id
      );
      if (
        response.code < 400 &&
        response.sessionToken &&
        response.paymentMethodConfigId
      ) {
        setPaymentConfig({
          sessionToken: response.sessionToken,
          paymentMethodConfigId: response.paymentMethodConfigId,
          billAt: billAt,
          hasBaseFee: baseFeeInCents > 0,
          hasNoShowFee: noShowFeeInCents > 0,
          hasLateCancelFee: lateCancelFeeInCents > 0,
        });
        setPaymentStatus(null);
        setPaymentMethodId(null);
      } else {
        enqueueSnackbar("Error setting up payment. Please try again.", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching payment method config:", error);
      enqueueSnackbar("Error setting up payment. Please try again.", {
        variant: "error",
      });
    }
  };

  const handleEventChange = async (event, formProps) => {
    setSettingsFetching();

    // Reset payment when event changes
    setPaymentConfig(null);
    setPaymentStatus(null);
    setPaymentMethodId(null);
    setBillingAcknowledged(false);
    setPaymentPending(false);

    // Fetch payment config if event has any billing fees (base, no-show, or late cancel)
    const billing = getBillingInfo(event);
    if (requiresPaymentCapture(billing)) {
      await fetchPaymentConfigForEvent(event);
    }

    const practitionerOptions = props.presetPractitionerId
      ? [props.presetPractitionerId]
      : event.event.practitioners;
    const practitioners = await getPractitioners(practitionerOptions);
    if (practitioners.code >= 400) {
      fetchPractitionersFailed();
      const message = "Error getting practitioners";
      enqueueSnackbar(message, {
        variant: "error",
      });
      return;
    } else {
      fetchPractitionersSuccess();
      setPractitioners(practitioners.data);
    }

    if (practitioners.data.length > 1) {
      return;
    } else if (practitioners.data.length === 1) {
      setSettingsFetching();
      const scheduleResponse = await getPractitionerSchedule(
        practitioners.data[0].practitionerId
      );
      if (scheduleResponse.code < 400 && scheduleResponse.scheduleId) {
        props.fetchScheduleSuccess();
        scheduleId = scheduleResponse.scheduleId;
      } else {
        props.fetchScheduleFailed();
        enqueueSnackbar("No schedule for clinician", {
          variant: "error",
        });
        if (formProps) {
          formProps.setFieldValue("practitioner", null);
        }
        return;
      }

      const availabilityResponse = await getAvailabilityForPractitioner(
        practitioners.data[0].practitionerId
      );
      if (availabilityResponse.code >= 400) {
        fetchAvailableSlotsFailed();
        const message = "Error getting available appointments";
        enqueueSnackbar(message, {
          variant: "error",
        });
        return;
      }

      availability = availabilityResponse.availability
        ? availabilityResponse.availability
        : practitioners.data[0].availability;
      practitionerUserRef.current = availabilityResponse.practitionerUser;
      setPractitionerUser(availabilityResponse.practitionerUser);
      currentEventIdRef.current = event.event._id;
      setAvailableDates(null);
      fetchMonthAvailability(
        practitioners.data[0].practitionerId,
        event.event._id
      );
      handlePickDate(
        calendarDate,
        formProps,
        event,
        availabilityResponse.practitionerUser
      );
    } else {
      const message = "No available practitioners";
      enqueueSnackbar(message, {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    let matchingOptions = [];

    for (const option of events) {
      const matchesEvent = props.presetEventId
        ? option._id === props.presetEventId
        : true;

      const matchesPractitioner = props.presetPractitionerId
        ? option.practitioners.includes(props.presetPractitionerId)
        : true;

      const visibleToPatientType =
        !option.archived &&
        ((option.availableToNewPatients && newPatient) ||
          (option.availableToExistingPatients && !newPatient));

      if (matchesEvent && matchesPractitioner && visibleToPatientType) {
        matchingOptions.push(option);
        // before handleEventChange to accomodate break
        if (props.presetPractitionerId) {
          handlePractitionerChange({ value: props.presetPractitionerId });
        }
        if (props.presetEventId) {
          handleEventChange({ event: option });
          break;
        }
      }
    }
    setEventOptions(matchingOptions);
  }, []);

  // Setup payment component and event listeners
  useEffect(() => {
    if (!paymentConfig) {
      paymentComponentRef.current = null;
      return;
    }

    let paymentEl = null;
    let cleanup = null;

    // Wait for custom element to be defined
    const setupPaymentComponent = () => {
      const container = document.getElementById("rainforest-payment-container");
      if (!container) return;

      // Clear previous component
      container.innerHTML = "";

      // Create payment component for saving card (payment-method-config, not payin-config)
      // All charging happens server-side
      paymentEl = document.createElement("rainforest-payment");
      paymentEl.setAttribute("session-key", paymentConfig.sessionToken);
      paymentEl.setAttribute(
        "payment-method-config-id",
        paymentConfig.paymentMethodConfigId
      );
      // Hide the built-in submit button - we'll use our own
      paymentEl.setAttribute("hide-button", "true");
      container.appendChild(paymentEl);
      paymentComponentRef.current = paymentEl;
      paymentValidRef.current = false;

      // Setup event listeners
      // With payment-method-config, the component only saves the card and returns payment_method_id
      const handleApproved = event => {
        // Rainforest returns event.detail as an ARRAY with the result at index 0
        // For payment-method-config: event.detail[0].data.payment_method_id
        let resultPaymentMethodId = null;

        if (Array.isArray(event.detail) && event.detail.length > 0) {
          // Handle array structure: event.detail[0].data
          const firstResult = event.detail[0];
          if (firstResult && firstResult.data) {
            resultPaymentMethodId = firstResult.data.payment_method_id || null;
          }
        } else if (event.detail && typeof event.detail === "object") {
          // Fallback: handle object structure (in case Rainforest changes the format)
          const detail = event.detail;
          const data = detail.data || {};
          resultPaymentMethodId =
            detail.payment_method_id ||
            data.payment_method_id ||
            detail.paymentMethodId ||
            null;
        }

        if (!resultPaymentMethodId) {
          // Approved with nothing we can charge later. Booking now would create a
          // billable appointment with no payment method on file, so fail instead.
          console.error(
            "Rainforest approved without a payment_method_id",
            event.detail
          );
          setPaymentStatus("error");
          const errorMsg =
            "We couldn't save your payment details. Please try again.";
          enqueueSnackbar(errorMsg, { variant: "error" });
          settlePayment("reject", new Error(errorMsg));
          return;
        }

        setPaymentMethodId(resultPaymentMethodId);
        setPaymentStatus("approved");

        // Resolve the pending promise so handleSubmit can use the value directly
        // This avoids React state timing issues
        settlePayment("resolve", { paymentMethodId: resultPaymentMethodId });
      };

      const handleDeclined = () => {
        setPaymentStatus("declined");
        const errorMsg = "Payment was declined. Please try again.";
        enqueueSnackbar(errorMsg, { variant: "error" });
        settlePayment("reject", new Error(errorMsg));
      };

      const handleError = () => {
        setPaymentStatus("error");
        const errorMsg = "Error saving your payment details. Please try again.";
        enqueueSnackbar(errorMsg, { variant: "error" });
        settlePayment("reject", new Error(errorMsg));
      };

      const handleValid = () => {
        paymentValidRef.current = true;
      };
      const handleInvalid = () => {
        paymentValidRef.current = false;
      };
      const handleAttempted = () => {
        paymentAttemptedRef.current = true;
      };

      paymentEl.addEventListener("approved", handleApproved);
      paymentEl.addEventListener("declined", handleDeclined);
      paymentEl.addEventListener("error", handleError);
      paymentEl.addEventListener("valid", handleValid);
      paymentEl.addEventListener("invalid", handleInvalid);
      paymentEl.addEventListener("attempted", handleAttempted);

      cleanup = () => {
        if (paymentEl) {
          paymentEl.removeEventListener("approved", handleApproved);
          paymentEl.removeEventListener("declined", handleDeclined);
          paymentEl.removeEventListener("error", handleError);
          paymentEl.removeEventListener("valid", handleValid);
          paymentEl.removeEventListener("invalid", handleInvalid);
          paymentEl.removeEventListener("attempted", handleAttempted);
        }
      };
    };

    // Check if custom element is already defined
    if (customElements.get("rainforest-payment")) {
      setupPaymentComponent();
    } else {
      // Wait for custom element to be defined
      customElements.whenDefined("rainforest-payment").then(() => {
        setupPaymentComponent();
      });
    }

    return () => {
      if (cleanup) {
        cleanup();
      }
      paymentComponentRef.current = null;
    };
  }, [paymentConfig, enqueueSnackbar]);

  // Settle the pending payment promise once, always clearing the spinner
  const settlePayment = (outcome, value) => {
    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
      paymentTimeoutRef.current = null;
    }
    setPaymentPending(false);
    const resolve = paymentResolveRef.current;
    const reject = paymentRejectRef.current;
    paymentResolveRef.current = null;
    paymentRejectRef.current = null;
    if (outcome === "resolve") {
      if (resolve) resolve(value);
    } else if (reject) {
      reject(value);
    }
  };

  // Function to programmatically trigger payment and wait for result
  const triggerPayment = async () => {
    const component = paymentComponentRef.current;
    if (!component) {
      const message =
        "Payment form isn't ready. Please refresh the page and try again.";
      enqueueSnackbar(message, { variant: "error" });
      throw new Error(message);
    }

    // Show the spinner for the whole attempt, including the wait below
    setPaymentPending(true);

    // Rainforest runs a BIN lookup as soon as the card number is complete, and
    // reports the form as invalid until it returns. Give it a moment to land so
    // a fast click isn't mistaken for incomplete details.
    if (!paymentValidRef.current) {
      const waitUntil = Date.now() + BIN_LOOKUP_WAIT_MS;
      while (!paymentValidRef.current && Date.now() < waitUntil) {
        await new Promise(r => setTimeout(r, 100));
      }
    }

    // submit() is a no-op on an invalid form and emits nothing, which would
    // otherwise leave us waiting forever
    if (!paymentValidRef.current) {
      setPaymentPending(false);
      const message = "Please complete your payment details before booking.";
      enqueueSnackbar(message, { variant: "error" });
      throw new Error(message);
    }

    const resultPromise = new Promise((resolve, reject) => {
      // Store resolve/reject for event handlers to use
      paymentResolveRef.current = resolve;
      paymentRejectRef.current = reject;
      // Backstop so a dropped submit can never spin forever
      paymentTimeoutRef.current = setTimeout(() => {
        const message =
          "We couldn't confirm your payment details. Please try again.";
        enqueueSnackbar(message, { variant: "error" });
        settlePayment("reject", new Error(message));
      }, PAYMENT_RESULT_TIMEOUT_MS);
      // Trigger the payment submission
      paymentAttemptedRef.current = false;
      component.submit();
    });

    // Rainforest emits `attempted` as soon as it accepts a submit. If that never
    // arrives the submit was silently dropped and no result event is coming, so
    // fail now instead of waiting out the backstop.
    setTimeout(() => {
      if (!paymentAttemptedRef.current && paymentRejectRef.current) {
        console.error(
          "Rainforest dropped submit() without emitting attempted",
          {
            paymentStatus,
          }
        );
        const message =
          "We couldn't submit your payment details. Please try again.";
        enqueueSnackbar(message, { variant: "error" });
        settlePayment("reject", new Error(message));
      }
    }, ATTEMPT_ACK_MS);

    return resultPromise;
  };

  const handleDateChange = async (date, event, user = practitionerUser) => {
    getAvailableSlots();
    const timeSlotResponse = await getAvailableTimes(
      user.practitionerId,
      date,
      event.event._id,
      moment.tz.guess(),
      props.selectedPatient && props.selectedPatient.id
    );
    if (timeSlotResponse && timeSlotResponse.code < 400) {
      timeSlotOptions = timeSlotResponse.data ? timeSlotResponse.data : [];
    } else {
      fetchAvailableSlotsFailed();
      enqueueSnackbar(
        typeof timeSlotResponse.error === "string"
          ? timeSlotResponse.error
          : "Error retrieving availability",
        {
          variant: "error",
        }
      );
    }
    fetchAvailableSlotsSuccess();
  };

  const handlePractitionerChange = async (practitioner, formProps) => {
    if (!practitioner || !practitioner.value) {
      setAvailableDates(null);
      setPractitionerUser(null);
      practitionerUserRef.current = null;
      return;
    }
    setSettingsFetching();
    const scheduleResponse = await getPractitionerSchedule(practitioner.value);
    if (scheduleResponse.code < 400 && scheduleResponse.scheduleId) {
      scheduleId = scheduleResponse.scheduleId;
    } else {
      props.fetchScheduleFailed();
      enqueueSnackbar("No schedule for clinician", {
        variant: "error",
      });
      if (formProps) {
        formProps.setFieldValue("practitioner", null);
      }
      return;
    }
    const availabilityResponse = await getAvailabilityForPractitioner(
      practitioner.value
    );
    if (availabilityResponse.code >= 400) {
      fetchAvailableSlotsFailed();
      const message = "Error getting available appointments";
      enqueueSnackbar(message, {
        variant: "error",
      });
      return;
    } else {
      fetchAvailableSlotsSuccess();
      availability = availabilityResponse.availability;
      if (formProps) {
        const event = formProps && formProps.values && formProps.values.event;
        if (event && event.event) {
          currentEventIdRef.current = event.event._id;
          setAvailableDates(null);
          fetchMonthAvailability(practitioner.value, event.event._id);
        }
      }
      practitionerUserRef.current = availabilityResponse.practitionerUser;
      setPractitionerUser(availabilityResponse.practitionerUser);
    }
  };

  const handlePickDate = async (
    date,
    formProps,
    overrideEventField,
    user,
    retry = true
  ) => {
    if (!user && retry) {
      setTimeout(() => {
        handlePickDate(
          date,
          formProps,
          overrideEventField,
          practitionerUserRef.current,
          false
        );
      }, 500);
      return;
    }

    calendarDate = date;
    const utcDate = date.format(STANDARD_DATE_FORMAT_NO_TIME);
    await handleDateChange(
      utcDate,
      overrideEventField
        ? overrideEventField
        : formProps
        ? formProps.values.event
        : null,
      user
    );
    if (formProps) {
      formProps.setValues({
        ...formProps.values,
        event: overrideEventField
          ? overrideEventField
          : formProps
          ? formProps.values.event
          : null,
        date: utcDate,
        timeSlot: null,
      });
    }
  };

  const handleSubmit = async (values, actions) => {
    let currentPaymentMethodId = null;

    try {
      if (isObjEmpty(props.selectedPatient)) {
        enqueueSnackbar("An error occured. Please refresh page and try again", {
          variant: "error",
        });
        actions.setSubmitting(false);
        return;
      }

      // Validate all required questions first
      let allQuestionsAnswered = true;
      if (
        values.event &&
        values.event.event &&
        Array.isArray(values.event.event.questions) &&
        values.event.event.questions.length > 0
      ) {
        values.event.event.questions.forEach((question, index) => {
          if (
            question.isRequired &&
            (!values[`question-${index}`] ||
              !values[`question-${index}`].trim())
          ) {
            allQuestionsAnswered = false;
          }
        });
      }
      if (!allQuestionsAnswered) {
        enqueueSnackbar("Please enter a value for all required questions", {
          variant: "error",
        });
        actions.setSubmitting(false);
        return;
      }

      // Check if slot is still available to prevent race condition
      const checkAvailability = await getAvailableTimes(
        practitionerUser.practitionerId,
        values.date,
        values.event.event._id,
        moment.tz.guess(),
        props.selectedPatient && props.selectedPatient.id
      );

      if (checkAvailability.code >= 400) {
        enqueueSnackbar("Error checking availability", {
          variant: "error",
        });
        actions.setSubmitting(false);
        return;
      }

      const isSlotAvailable =
        checkAvailability.data &&
        checkAvailability.data.find(
          slot => slot.value === values.timeSlot.value
        );

      if (!isSlotAvailable) {
        enqueueSnackbar(
          "This appointment slot is no longer available. Please select another time.",
          {
            variant: "error",
          }
        );
        await handleDateChange(values.date, values.event, practitionerUser);
        actions.setSubmitting(false);
        return;
      }

      // Check if payment/card is required and process it
      const billing = getBillingInfo(values.event);
      const baseFeeInCents =
        billing && billing.baseFee ? billing.baseFee.amountInCents : 0;
      const noShowFeeInCents =
        billing && billing.noShowFee ? billing.noShowFee.amountInCents : 0;
      const lateCancelFeeInCents =
        billing && billing.lateCancelFee
          ? billing.lateCancelFee.amountInCents
          : 0;
      const billAt = getBillAt(billing);
      const hasOnlyPenaltyFees =
        baseFeeInCents === 0 &&
        (noShowFeeInCents > 0 || lateCancelFeeInCents > 0);

      // All billing scenarios now just save the card - server handles all charging
      if (paymentConfig && requiresPaymentCapture(billing)) {
        // For deferred billing or penalty-only fees, require acknowledgment
        if (billAt !== "purchase" || hasOnlyPenaltyFees) {
          if (!billingAcknowledged) {
            enqueueSnackbar(
              "Please acknowledge the billing terms before booking the appointment",
              { variant: "error" }
            );
            actions.setSubmitting(false);
            return;
          }
        }

        // Save card for all billing scenarios
        // Server will charge for "purchase" at booking time, or later for "service"/"completion"
        if (paymentStatus === "approved" && paymentMethodId) {
          // Saved on an earlier attempt whose booking then failed. Rainforest
          // won't accept a second submit() once it has approved, so reuse it.
          currentPaymentMethodId = paymentMethodId;
        } else {
          try {
            const paymentResult = await triggerPayment();
            currentPaymentMethodId = paymentResult.paymentMethodId;

            // Only show "saved" message for non-purchase (deferred) billing
            if (billAt !== "purchase") {
              enqueueSnackbar("Payment details saved successfully", {
                variant: "success",
              });
            }
          } catch (paymentError) {
            // Save failed - error already shown by triggerPayment/event handler
            actions.setSubmitting(false);
            return;
          }
        }
      }

      const endDateTime = moment(values.timeSlot.value)
        .add(values.event.event.duration, "minutes")
        .utc()
        .format();
      let slotId = generateUuid();
      if (!scheduleId) {
        enqueueSnackbar("Unable to schedule with clinician", {
          variant: "error",
        });
        actions.setSubmitting(false);
        return;
      }
      const appointmentDescription =
        values.event.event.description || values.event.event.name;
      let slot = createSlotResource(
        slotId,
        values.timeSlot.value,
        endDateTime,
        "busy",
        scheduleId,
        appointmentDescription
      );
      let appointment = createAppointmentResource(
        values.timeSlot.value,
        endDateTime,
        "booked",
        appointmentDescription,
        slotId,
        practitionerUser.practitionerId,
        props.selectedPatient,
        null
      );
      if (props.newPatient) {
        const patientResponse = await createNewPatientAndTask(
          props.selectedPatient,
          props.organization
        );
        if (patientResponse.code >= 400) {
          enqueueSnackbar(
            toErrorMessage(patientResponse.error, "Error creating patient"),
            {
              variant: "error",
            }
          );
          actions.setSubmitting(false);
          return;
        }
      }
      // Build apptMeta resource
      let apptMeta = await createApptMetaResource(
        appointment,
        slotId,
        values.event.event,
        values,
        practitionerUser,
        props.organization,
        props.selectedPatient,
        values.timeSlot.locationId
      );

      // Build billing object for server
      // Server will handle charging for "purchase" billing using the saved paymentMethodId
      let billingForServer = null;
      if (billing && requiresPaymentCapture(billing)) {
        billingForServer = {
          paymentMethodId: currentPaymentMethodId || null,
          baseFee: {
            amountInCents: baseFeeInCents,
            billAt: billAt || null,
          },
          noShowFee: {
            amountInCents: noShowFeeInCents,
          },
          lateCancelFee: {
            amountInCents: lateCancelFeeInCents,
          },
        };
      }

      // Use new unified endpoint that handles slot + appointment + apptMeta + billing
      let response = await createAppointmentWithBilling(
        slot,
        appointment,
        apptMeta,
        billingForServer
      );

      if (response.code < 400) {
        // Appointment created successfully
        // Server handles: charge (if purchase) → create slot → create appointment → create apptMeta
        if (response.data && response.data.apptMeta) {
          apptMeta = response.data.apptMeta;
        }
        let addressOrganization;
        if (
          !(
            props.organization.settings &&
            props.organization.settings.noAppointmentEmailLocation
          )
        ) {
          addressOrganization = props.organization;
          if (
            props.organization.settings &&
            props.organization.settings.multiLocations &&
            apptMeta.locationId
          ) {
            const locationResponse = await getLocation(apptMeta.locationId);
            const locationObj =
              locationResponse &&
              locationResponse.data &&
              locationResponse.code === 200
                ? locationResponse.data
                : null;
            addressOrganization =
              locationObj && locationObj.addressLine1
                ? locationObj
                : props.organization;
          }
        }
        actions.resetForm({
          event: null,
          date: null,
          timeSlot: null,
        });
        let message = "Successfully booked appointment";
        enqueueSnackbar(message, {
          variant: "success",
        });
        window.analytics.track("Appointment Self Scheduled");
        props.setAppointmentSuccess(
          values.event.event.name,
          values.event.event.description,
          appointment.start,
          appointment.end,
          apptMeta && apptMeta.videoVisit && apptMeta.videoUrl
            ? apptMeta.videoUrl
            : addressOrganization && addressOrganization.addressLine1
            ? `${addressOrganization.addressLine1}${
                addressOrganization.addressLine2
                  ? `, ${addressOrganization.addressLine2}`
                  : ""
              }, ${addressOrganization.city}, ${addressOrganization.state}, ${
                addressOrganization.zipcode
              }`
            : ""
        );
        if (values.event.event.sendPatientEmail) {
          sendEmailAppointment(
            props.newPatient,
            appointment,
            apptMeta,
            props.organization,
            addressOrganization,
            props.selectedPatient,
            values.event.event,
            availability,
            true
          );
        }
        let practitionerResponse = await getPractitioner(
          practitionerUser.practitionerId
        );
        if (practitionerResponse.code < 400) {
          sendEmailNotification(
            values.event.event.name,
            appointment.start,
            props.organization,
            addressOrganization,
            practitionerResponse.practitioner,
            props.selectedPatient,
            props.newPatient,
            apptMeta,
            availability
          );
        }
      } else {
        // Booking failed
        // Server handles all charging and refunds - if server charged and failed, it refunds automatically
        const errorMessage = toErrorMessage(
          (response.data && response.data.error) || response.message,
          "Booking failed"
        );
        enqueueSnackbar(errorMessage, {
          variant: "error",
        });
        window.analytics.track("Error Submitting Appointment");
      }
      actions.setSubmitting(false);
    } catch (error) {
      // Server handles all charging and refunds
      enqueueSnackbar("Error booking appointment", {
        variant: "error",
      });
      actions.setSubmitting(false);
    }
  };

  let initialValues = {
    event:
      props.presetEventId && eventOptions.length > 0
        ? {
            event: eventOptions[0],
            label: eventOptions[0].name,
            value: eventOptions[0]._id,
          }
        : null,
    date: null,
    timeSlot: null,
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        className={classes.form}
        validationSchema={AppointmentOptionsSchema}
        onSubmit={handleSubmit}
        id="appointmentOptionsForm"
        enableReinitialize={true}
        render={formProps => (
          <Form className="min-h-full h-auto mx-auto w-full max-w-5xl bg-white shadow-md rounded px-8 py-8 pt-8">
            <div className="mx-auto w-full max-w-xl">
              <h2 className="text-lg font-bold py-2">Book Appointment</h2>
              {(fetching && !formProps.values.event) ||
                (fetching && !availability && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                ))}
              {props.settingsFetched && error === null && (
                <Grid container className={classes.container} spacing={8}>
                  {eventOptions.length > 0 ? (
                    <Grid item xs={12}>
                      <EventSelectForm
                        events={eventOptions}
                        selectedEvent={formProps.values.event}
                        label="Select Appointment Type"
                        name="event"
                        onChange={async event => {
                          formProps.setValues({
                            event: event,
                            date: null,
                            timeSlot: null,
                          });
                          availability = null;
                          setAvailableDates(null);
                          setPractitioners([]);
                          // Reset payment when event changes
                          setPaymentConfig(null);
                          setPaymentStatus(null);
                          setPaymentMethodId(null);
                          setBillingAcknowledged(false);
                          setPaymentPending(false);
                          // Fetch payment config if event has any billing fees
                          const billing = getBillingInfo(event);
                          if (requiresPaymentCapture(billing)) {
                            await fetchPaymentConfigForEvent(event);
                          }
                          handleEventChange(event, formProps);
                        }}
                        practitionerUser={practitionerUser}
                      />
                      <ErrorMessage
                        name="event"
                        component="span"
                        className={errorClass}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <h4>{noEventsMessage}</h4>
                    </Grid>
                  )}
                  {formProps.values.event &&
                    formProps.values.event.event &&
                    Array.isArray(practitioners) &&
                    practitioners.length > 1 && (
                      <Grid item xs={12}>
                        <Field
                          label="Select Practitioner"
                          classes={classes.select}
                          fullWidth
                          name="practitioner"
                          component={dropdown}
                          options={practitioners.map(practitioner => {
                            return {
                              value: practitioner.practitionerId,
                              label: parseUserName(practitioner),
                            };
                          })}
                          placeholder="Select..."
                          variant="outlined"
                          // defaultValue={formProps.values.timeSlot}
                          value={formProps.values.practitioner}
                          onChange={practitioner => {
                            formProps.setValues({
                              ...formProps.values,
                              practitioner,
                              date: null,
                              timeSlot: null,
                            });
                            setAvailableDates(null);
                            handlePractitionerChange(practitioner, formProps);
                          }}
                        />
                        <ErrorMessage
                          name="practitioner"
                          component="span"
                          className={errorClass}
                        />
                      </Grid>
                    )}
                  {formProps.values.event && fetchingDates && (
                    <Grid item xs={12}>
                      <div className="rounded border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center justify-center gap-2 py-2 text-gray-600 text-sm">
                          <CircularProgress size={18} />
                          <span>
                            Loading availability for{" "}
                            {calendarDate.format("MMMM YYYY")}...
                          </span>
                        </div>
                      </div>
                    </Grid>
                  )}
                  {formProps.values.event &&
                    !fetchingDates &&
                    availableDates !== null &&
                    hasBookableSlotsInRange(formProps.values.event) && (
                      <React.Fragment>
                        <Grid item xs={12}>
                          <Field
                            component={renderCalendarPicker}
                            name="calendarDate"
                            autoOk
                            orientation="landscape"
                            shouldDisableDate={date => {
                              const dateStr = moment(date).format("YYYY-MM-DD");
                              if (availableDates) {
                                return (
                                  !availableDates[dateStr] ||
                                  availableDates[dateStr].length === 0
                                );
                              }
                              return false;
                            }}
                            maxDate={moment().add(
                              formProps.values.event.event.dateRange,
                              "days"
                            )}
                            date={calendarDate}
                            showTodayButton
                            onMonthChange={newMonth => {
                              calendarDate = newMonth;
                              if (
                                practitionerUser &&
                                currentEventIdRef.current
                              ) {
                                fetchMonthAvailability(
                                  practitionerUser.practitionerId,
                                  currentEventIdRef.current,
                                  newMonth
                                );
                              }
                            }}
                            onChange={value => {
                              handlePickDate(
                                value,
                                formProps,
                                formProps.values.event,
                                practitionerUser
                              );
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            name="date"
                            shouldDisableDate={date => {
                              const dateStr = moment(date).format("YYYY-MM-DD");
                              if (availableDates) {
                                return (
                                  !availableDates[dateStr] ||
                                  availableDates[dateStr].length === 0
                                );
                              }
                              return false;
                            }}
                            component={date}
                            variant="outlined"
                            label="Select Date"
                            onChange={date => {
                              handlePickDate(date, formProps);
                            }}
                            maxDate={moment().add(
                              formProps.values.event.event.dateRange,
                              "days"
                            )}
                            enablePast={false}
                          />
                        </Grid>
                        <Hidden smDown>
                          <Grid item xs={false} />
                        </Hidden>
                        {fetching && (
                          <Grid item xs={3}>
                            <br />
                            <br />
                            <CircularProgress
                              size={24}
                              className={classes.buttonProgress}
                            />
                          </Grid>
                        )}
                      </React.Fragment>
                    )}
                  {formProps.values.event &&
                    !fetchingDates &&
                    availableDates !== null &&
                    !hasBookableSlotsInRange(formProps.values.event) &&
                    (() => {
                      const nextMonth = moment(calendarDate).add(1, "month");
                      const prevMonth = moment(calendarDate).subtract(
                        1,
                        "month"
                      );
                      const maxD = moment().add(
                        formProps.values.event.event.dateRange,
                        "days"
                      );
                      const today = moment();
                      const canCheckNextMonth = nextMonth
                        .clone()
                        .startOf("month")
                        .isSameOrBefore(maxD, "day");
                      const canCheckPrevMonth = prevMonth
                        .clone()
                        .endOf("month")
                        .isSameOrAfter(today, "day");
                      const goToMonth = target => {
                        calendarDate = target;
                        if (practitionerUser && currentEventIdRef.current) {
                          fetchMonthAvailability(
                            practitionerUser.practitionerId,
                            currentEventIdRef.current,
                            target
                          );
                        }
                      };
                      return (
                        <Grid item xs={12}>
                          <div className="rounded border border-gray-200 bg-gray-50 p-4">
                            <p className="text-gray-800 font-medium mb-3">
                              No availability in{" "}
                              {calendarDate.format("MMMM YYYY")} for the
                              requested provider.
                            </p>
                            {(canCheckPrevMonth || canCheckNextMonth) && (
                              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-3">
                                {canCheckPrevMonth && (
                                  <button
                                    type="button"
                                    onClick={() => goToMonth(prevMonth)}
                                    className="inline-flex items-center justify-center w-full sm:w-auto bg-white hover:bg-gray-100 border border-gray-300 text-gray-800 font-medium py-2 px-4 rounded"
                                  >
                                    <span aria-hidden="true" className="mr-2">
                                      ‹
                                    </span>
                                    {prevMonth.format("MMMM YYYY")}
                                  </button>
                                )}
                                {canCheckNextMonth && (
                                  <button
                                    type="button"
                                    onClick={() => goToMonth(nextMonth)}
                                    className="inline-flex items-center justify-center w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                                  >
                                    {nextMonth.format("MMMM YYYY")}
                                    <span aria-hidden="true" className="ml-2">
                                      ›
                                    </span>
                                  </button>
                                )}
                              </div>
                            )}
                            <p className="text-gray-600 text-sm">
                              {canCheckPrevMonth || canCheckNextMonth
                                ? "Or contact"
                                : "Please contact"}{" "}
                              the clinic directly at{" "}
                              {props.organization.phoneNumber}.
                            </p>
                          </div>
                        </Grid>
                      );
                    })()}
                  {formProps.values.date &&
                    formProps.values.event &&
                    scheduleFetched &&
                    hasBookableSlotsInRange(formProps.values.event) && (
                      <Grid item xs={12}>
                        <br />
                        <Field
                          classes={classes.select}
                          fullWidth
                          name="timeSlot"
                          component={dropdown}
                          label={"Timeslot - " + timeZone}
                          options={timeSlotOptions}
                          placeholder={
                            timeSlotOptions.length
                              ? "Select..."
                              : "No available slots for this day"
                          }
                          variant="outlined"
                          defaultValue={formProps.values.timeSlot}
                          value={formProps.values.timeSlot}
                          onChange={value => {
                            formProps.setFieldValue("timeSlot", value, true);
                            formProps.setFieldValue(
                              "reminderTime",
                              moment(value.value)
                                .subtract(24, "hours")
                                .utc()
                                .format(),
                              true
                            );
                          }}
                        />
                        <ErrorMessage
                          name="timeSlot"
                          component="span"
                          className={errorClass}
                        />
                      </Grid>
                    )}
                  {formProps.values.event &&
                    formProps.values.event.event &&
                    Array.isArray(formProps.values.event.event.questions) &&
                    formProps.values.event.event.questions.length > 0 &&
                    formProps.values.event.event.questions.map(
                      (question, index) => (
                        <Grid item xs={12} key={index}>
                          <Field
                            classes={classes.paper}
                            fullWidth
                            name={`question-${index}`}
                            label={question.text}
                            component={text}
                            variant="outlined"
                            required={question.isRequired}
                          />
                          <ErrorMessage
                            name={`question-${index}`}
                            component="span"
                            className={classes.error}
                          />
                        </Grid>
                      )
                    )}
                  {(() => {
                    const eventBilling = getBillingInfo(formProps.values.event);
                    // Check if any billing fees exist (not just base fee)
                    const hasBilling =
                      formProps.values.event &&
                      formProps.values.event.event &&
                      eventBilling &&
                      requiresPaymentCapture(eventBilling);
                    if (!hasBilling) return null;

                    const baseFee = getBaseFeeInDollars(eventBilling);
                    const noShowFee = getNoShowFeeInDollars(eventBilling);
                    const lateCancelFee = getLateCancelFeeInDollars(
                      eventBilling
                    );
                    const billAt = getBillAt(eventBilling);
                    const hasBaseFee = baseFee > 0;
                    const hasOnlyPenaltyFees =
                      !hasBaseFee && (noShowFee > 0 || lateCancelFee > 0);

                    return (
                      <Grid item xs={12}>
                        <div
                          style={{
                            marginTop: "20px",
                            marginBottom: "20px",
                            padding: "16px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "8px",
                          }}
                        >
                          {/* Header based on fee type */}
                          {hasOnlyPenaltyFees ? (
                            <>
                              <h4 style={{ marginBottom: "10px" }}>
                                Card Required for Policy Fees
                              </h4>
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: "#666",
                                  marginBottom: "16px",
                                }}
                              >
                                Please save your card information below. Your
                                card will only be charged if you miss your
                                appointment or cancel late.
                              </p>
                            </>
                          ) : billAt === "purchase" ? (
                            <>
                              <h4 style={{ marginBottom: "10px" }}>
                                Payment Required: ${baseFee.toFixed(2)}
                              </h4>
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: "#666",
                                  marginBottom: "16px",
                                }}
                              >
                                Your card will be charged upon booking.
                              </p>
                            </>
                          ) : (
                            <>
                              <h4 style={{ marginBottom: "10px" }}>
                                Appointment Fee: ${baseFee.toFixed(2)}
                              </h4>
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: "#666",
                                  marginBottom: "16px",
                                }}
                              >
                                Your card will be charged{" "}
                                {billAt === "service"
                                  ? "at the time of your appointment"
                                  : "after your appointment is completed"}
                                . Please save your card information below.
                              </p>
                            </>
                          )}

                          {/* No-show fee notice */}
                          {noShowFee > 0 && (
                            <div
                              style={{
                                backgroundColor: "#fff3cd",
                                border: "1px solid #ffc107",
                                borderRadius: "4px",
                                padding: "12px",
                                marginBottom: "16px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: "#856404",
                                  margin: 0,
                                }}
                              >
                                <strong>No-Show Fee:</strong> A fee of $
                                {noShowFee.toFixed(2)} will be charged if you do
                                not show up for your appointment without prior
                                notice.
                              </p>
                            </div>
                          )}

                          {/* Late cancellation fee notice */}
                          {lateCancelFee > 0 && (
                            <div
                              style={{
                                backgroundColor: "#fff3cd",
                                border: "1px solid #ffc107",
                                borderRadius: "4px",
                                padding: "12px",
                                marginBottom: "16px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: "#856404",
                                  margin: 0,
                                }}
                              >
                                <strong>Late Cancellation Fee:</strong> A fee of
                                ${lateCancelFee.toFixed(2)} will be charged if
                                you cancel your appointment without sufficient
                                notice.
                              </p>
                            </div>
                          )}

                          {paymentConfig ? (
                            <div>
                              <div id="rainforest-payment-container"></div>
                              {(paymentStatus === "declined" ||
                                paymentStatus === "error") && (
                                <p
                                  style={{
                                    color: "red",
                                    marginTop: "10px",
                                  }}
                                >
                                  {paymentStatus === "declined"
                                    ? "Payment declined. Please check your payment details and try again."
                                    : "An error occurred. Please try again."}
                                </p>
                              )}
                            </div>
                          ) : (
                            <CircularProgress size={24} />
                          )}

                          {/* Acknowledgment checkbox for deferred billing or penalty-only fees */}
                          {(billAt !== "purchase" || hasOnlyPenaltyFees) && (
                            <div style={{ marginTop: "16px" }}>
                              <label
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={billingAcknowledged}
                                  onChange={e =>
                                    setBillingAcknowledged(e.target.checked)
                                  }
                                  style={{
                                    marginRight: "10px",
                                    marginTop: "4px",
                                  }}
                                />
                                <span style={{ fontSize: "14px" }}>
                                  {hasOnlyPenaltyFees ? (
                                    <>
                                      I understand that my card will be saved
                                      and may be charged
                                      {noShowFee > 0 &&
                                        ` a $${noShowFee.toFixed(
                                          2
                                        )} fee if I do not show up`}
                                      {noShowFee > 0 &&
                                        lateCancelFee > 0 &&
                                        " or"}
                                      {lateCancelFee > 0 &&
                                        ` a $${lateCancelFee.toFixed(
                                          2
                                        )} fee if I cancel late`}
                                      .
                                    </>
                                  ) : (
                                    <>
                                      I understand that my card will be charged
                                      ${baseFee.toFixed(2)}{" "}
                                      {billAt === "service"
                                        ? "at the time of my appointment"
                                        : "after my appointment is completed"}
                                      {noShowFee > 0 &&
                                        `, and I acknowledge that a $${noShowFee.toFixed(
                                          2
                                        )} fee will be charged if I do not show up`}
                                      {lateCancelFee > 0 &&
                                        `${
                                          noShowFee > 0
                                            ? ", and"
                                            : ", and I acknowledge that"
                                        } a $${lateCancelFee.toFixed(
                                          2
                                        )} fee will be charged if I cancel late`}
                                      .
                                    </>
                                  )}
                                </span>
                              </label>
                            </div>
                          )}
                        </div>
                      </Grid>
                    );
                  })()}
                </Grid>
              )}
              {!error && fetched && eventOptions.length > 0 && (
                <React.Fragment>
                  <br />
                  <Field
                    name="submit"
                    component={renderSubmitButton}
                    disabled={(() => {
                      // Check if required form fields are filled
                      if (
                        !formProps.values.event ||
                        !formProps.values.date ||
                        !formProps.values.timeSlot
                      ) {
                        return true;
                      }

                      // Check if no availability
                      if (
                        availableDates !== null &&
                        Object.values(availableDates).every(
                          slots => slots.length === 0
                        )
                      ) {
                        return true;
                      }

                      // Check billing requirements
                      const billing = getBillingInfo(formProps.values.event);
                      const baseFeeInCents =
                        billing && billing.baseFee
                          ? billing.baseFee.amountInCents
                          : 0;
                      const noShowFeeInCents =
                        billing && billing.noShowFee
                          ? billing.noShowFee.amountInCents
                          : 0;
                      const lateCancelFeeInCents =
                        billing && billing.lateCancelFee
                          ? billing.lateCancelFee.amountInCents
                          : 0;
                      const billAt = getBillAt(billing);
                      const hasPenaltyFees =
                        noShowFeeInCents > 0 || lateCancelFeeInCents > 0;
                      const hasOnlyPenaltyFees =
                        baseFeeInCents === 0 && hasPenaltyFees;

                      // If any billing fees exist, payment config must be ready
                      if (requiresPaymentCapture(billing) && !paymentConfig) {
                        return true;
                      }

                      // For deferred billing or penalty-only fees, must have acknowledged
                      if (baseFeeInCents > 0) {
                        if (
                          (billAt === "service" || billAt === "completion") &&
                          !billingAcknowledged
                        ) {
                          return true;
                        }
                      } else if (hasOnlyPenaltyFees && !billingAcknowledged) {
                        return true;
                      }

                      // Check if payment is currently processing
                      if (paymentPending) {
                        return true;
                      }

                      return false;
                    })()}
                  >
                    {(() => {
                      if (paymentPending) {
                        return "Processing...";
                      }
                      const billing = getBillingInfo(formProps.values.event);
                      const baseFeeInCents =
                        billing && billing.baseFee
                          ? billing.baseFee.amountInCents
                          : 0;
                      const billAt = getBillAt(billing);

                      if (baseFeeInCents > 0) {
                        if (billAt === "purchase") {
                          return "Book & Pay";
                        }
                        return "Book Appointment";
                      }
                      return "Book Appointment";
                    })()}
                  </Field>
                </React.Fragment>
              )}
            </div>
          </Form>
        )}
      />
    </div>
  );
};

const mapStateToProps = state => {
  return {
    events: state.settings.events,
    fetching: state.settings.fetching || state.schedule.fetching,
    fetched: state.settings.fetched && state.schedule.fetched,
    scheduleFetched: state.schedule.fetched,
    settingsFetched: state.settings.fetched,
    error: state.schedule.error || state.settings.error,
    selectedPatient: state.patients.selectedPatient,
    newPatient: state.patients.newPatient,
    organization: state.settings.organization,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    getAvailableSlots: () => dispatch({ type: REQUEST_AVAILABLE_SLOTS }),
    fetchAvailableSlotsSuccess: () =>
      dispatch({ type: FETCH_AVAILABLE_SLOTS_SUCCESS }),
    setSettingsFetching: () => dispatch({ type: SET_SCHEDULE_FETCHING }),
    fetchScheduleFailed: () => dispatch({ type: FETCH_SCHEDULE_FAILED }),
    fetchScheduleSuccess: () => dispatch({ type: FETCH_SCHEDULE_SUCCEEDED }),
    // clearStore: () => dispatch({ type: RESET_STORE }),,
    setAppointmentSuccess: (title, description, startTime, endTime, location) =>
      dispatch({
        type: SET_APPOINTMENT_SUCCESS,
        title: title,
        description: description,
        startTime: startTime,
        endTime: endTime,
        location: location,
      }),
    fetchAvailableSlotsFailed: () =>
      dispatch({ type: FETCH_AVAILABLE_SLOTS_FAILED }),
    fetchPractitionersSuccess: () =>
      dispatch({ type: FETCH_PRACTITIONERS_SUCCEEDED }),
    fetchPractitionersFailed: () =>
      dispatch({ type: FETCH_PRACTITIONERS_FAILED }),
  };
};

// AppointmentOptionsForm.propTypes = {
//   practitionerId: PropTypes.string.isRequired,
// };

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(withSnackbar(AppointmentOptionsForm)));
